import { useEffect, useRef } from "react";
import { WS_URL } from "@/lib/api";

export const STATUS = {
  todo: { label: "À faire", cls: "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400", dot: "bg-yellow-500" },
  enroute: { label: "En route", cls: "text-indigo-600 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400", dot: "bg-indigo-500" },
  in_progress: { label: "En cours", cls: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400", dot: "bg-blue-500" },
  completed: { label: "Terminée", cls: "text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400", dot: "bg-emerald-500" },
  cancelled: { label: "Annulée", cls: "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400", dot: "bg-red-500" },
};
export const STATUS_FLOW = ["todo", "enroute", "in_progress", "completed"];

export const DOC_STATUS = {
  draft: { label: "Brouillon", cls: "text-muted-foreground bg-muted" },
  sent: { label: "Envoyé", cls: "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400" },
  paid: { label: "Payé", cls: "text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400" },
};

// Clés stockées en base (voir PAYMENT_METHODS dans server.py) → libellés affichés.
export const PAYMENT_METHODS = [
  { value: "especes", label: "Espèces" },
  { value: "cb", label: "CB" },
  { value: "virement", label: "Virement" },
  { value: "cheque", label: "Chèque" },
  { value: "autre", label: "Autre" },
];
export const paymentLabel = (key) => PAYMENT_METHODS.find((p) => p.value === key)?.label || "";

// Frais de société (lot 20) : palette des catégories de dépenses (mêmes clés que EXPENSE_COLORS dans
// server.py). Classes écrites en entier pour que Tailwind les garde au build.
export const EXPENSE_COLORS = {
  blue: { label: "Bleu", dot: "bg-blue-500", pill: "text-blue-700 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400" },
  violet: { label: "Violet", dot: "bg-violet-500", pill: "text-violet-700 bg-violet-100 dark:bg-violet-900/30 dark:text-violet-400" },
  green: { label: "Vert", dot: "bg-green-500", pill: "text-green-700 bg-green-100 dark:bg-green-900/30 dark:text-green-400" },
  orange: { label: "Orange", dot: "bg-orange-500", pill: "text-orange-700 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400" },
  gray: { label: "Gris", dot: "bg-gray-400", pill: "text-gray-700 bg-gray-100 dark:bg-gray-800 dark:text-gray-300" },
  red: { label: "Rouge", dot: "bg-red-500", pill: "text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-400" },
  amber: { label: "Ambre", dot: "bg-amber-500", pill: "text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400" },
  pink: { label: "Rose", dot: "bg-pink-500", pill: "text-pink-700 bg-pink-100 dark:bg-pink-900/30 dark:text-pink-400" },
  teal: { label: "Turquoise", dot: "bg-teal-500", pill: "text-teal-700 bg-teal-100 dark:bg-teal-900/30 dark:text-teal-400" },
  sky: { label: "Ciel", dot: "bg-sky-500", pill: "text-sky-700 bg-sky-100 dark:bg-sky-900/30 dark:text-sky-400" },
};
export const expenseColor = (key) => EXPENSE_COLORS[key] || EXPENSE_COLORS.gray;

export const money = (n, cur = "EUR") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur }).format(n || 0);

export const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
export const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

// Heure relative de la cloche : « à l'instant », « il y a 5 min », « il y a 3 h », « hier à 09:00 », « 12/10 à 09:00 ».
export const timeAgo = (iso, now = new Date()) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(0, (now - d) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  const heure = fmtTime(iso);
  const hier = new Date(now); hier.setDate(hier.getDate() - 1);
  if (d.toDateString() === hier.toDateString()) return `hier à ${heure}`;
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} à ${heure}`;
};

// Date du jour (AAAA-MM-JJ) dans le fuseau des Réglages, pas en UTC ni selon l'ordinateur.
export const todayIn = (tz) => {
  try { return new Intl.DateTimeFormat("en-CA", { timeZone: tz || "Europe/Monaco" }).format(new Date()); }
  catch { return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Monaco" }).format(new Date()); }
};

export const missionLabel = (m) =>
  m.type === "Autre" && m.type_detail ? m.type_detail : m.type;

export function useWebSocket(onMessage) {
  const ref = useRef(onMessage);
  ref.current = onMessage;
  useEffect(() => {
    let ws, timer, closed = false;
    const connect = () => {
      const token = localStorage.getItem("token");
      ws = new WebSocket(WS_URL + (token ? `?token=${encodeURIComponent(token)}` : ""));
      ws.onmessage = (e) => {
        try { const d = JSON.parse(e.data); if (d.type !== "ping") ref.current(d); } catch {}
      };
      ws.onclose = () => { if (!closed) timer = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => { closed = true; clearTimeout(timer); try { ws && ws.close(); } catch {} };
  }, []);
}

export function usePoll(fn, ms = 12000) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const id = setInterval(() => ref.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}
